import {
  ConfigurationGroupBasicModel,
  useListConfigurationGroupsQuery,
} from '@casops/store/apis/configurationGroupsApi';
import {
  RegisteredAssetBasicModel,
  useListAssetsQuery,
} from '@casops/store/apis/registeredAssetsApi';
import { useAppSelector } from '@casops/store/hooks';
import { useMemo } from 'react';

export interface UseAssetsAssignConfigurationGroupLoadModelDataReturn {
  isLoaded: boolean;
  configurationGroups?: ConfigurationGroupBasicModel[];
  defaultConfigurationId: RegisteredAssetBasicModel['cfg'] | null;
  selectedAssetIds: number[] | undefined;
}

export const useAssetsAssignConfigurationGroupLoadModelData =
  (): UseAssetsAssignConfigurationGroupLoadModelDataReturn => {
    const { sidePanelBulkEditMode, sidePanelBulkModelIds } = useAppSelector(state => state.global);
    const selectedAssetIds = sidePanelBulkModelIds?.map(Number);
    const editMode = sidePanelBulkEditMode;

    const { data: configurationGroups, isSuccess: isSuccessConfigurationGroups } =
      useListConfigurationGroupsQuery(undefined, {
        refetchOnMountOrArgChange: 60,
      });

    const { data, isSuccess: isSucessAssets } = useListAssetsQuery(undefined, {
      refetchOnMountOrArgChange: 60,
    });
    const { items: assets } = data ?? {};

    const defaultConfigurationId = useMemo((): number | null => {
      if (!selectedAssetIds?.length || !assets?.length) return null;

      // Get all configuration group IDs for selected asset IDs
      const cfgIds = selectedAssetIds
        .map(id => assets.find(asset => asset.id === id)?.cfg)
        .filter((cfg): cfg is number => cfg !== undefined);

      // If no configuration group IDs found, return null
      if (cfgIds.length === 0) return null;

      // If all configuration group IDs are the same, return that configuration group ID; otherwise, force user to choose
      return cfgIds.every(cfgId => cfgId === cfgIds[0]) ? cfgIds[0] : null;
    }, [assets, selectedAssetIds]);

    const isLoaded = useMemo<boolean>(() => {
      let isLoaded = false;

      // Confirm that the Edit form data-fetching requirements are met.
      if (
        editMode &&
        isSuccessConfigurationGroups &&
        isSucessAssets &&
        configurationGroups &&
        assets
      ) {
        isLoaded = true;
      }
      // The create form has no further data-fetching requirements.
      else if (!editMode) {
        isLoaded = true;
      }

      return isLoaded;
    }, [assets, configurationGroups, editMode, isSuccessConfigurationGroups, isSucessAssets]);

    return {
      isLoaded,
      configurationGroups,
      defaultConfigurationId,
      selectedAssetIds,
    };
  };

export default useAssetsAssignConfigurationGroupLoadModelData;
